import {
  IAcademicsAssessmentCourseMap,
  IAcademicsAssessment,
  EAssessmentType,
  EAssessmentStatus,
  IAcademicsAssessmentStatistics,
} from '../../apis/academics/academicsAssessments.interfaces';
import { mockUserAction } from '../users/userAction';
import { mockAcademicsCourses } from './academicsCourses.mock';

// Helper function to generate assessments for a course
const generateCourseAssessments = (
  courseId: number,
  courseName: string
): IAcademicsAssessment[] => {
  const assessments: IAcademicsAssessment[] = [];

  // Generate 2-3 exams per course
  for (let i = 1; i <= 2; i++) {
    const avgScore = Math.floor(Math.random() * 30) + 70; // 70-100
    assessments.push({
      assessmentId: `ASSESS-COURSE-${courseId}-EXAM-${i}`,
      name: `${courseName} - Unit ${i} Exam`,
      description: `Unit ${i} examination for ${courseName}`,
      assessmentType: EAssessmentType.EXAM,
      status: i === 1 ? EAssessmentStatus.GRADED : EAssessmentStatus.SCHEDULED,
      courseId,
      maxScore: 100,
      totalStudents: Math.floor(Math.random() * 10) + 20, // 20-30 students
      averageScore: avgScore,
      averagePercentage: avgScore,
      scheduledDate: `2024-0${3 + i}-15T09:00:00.000Z`,
      conductedDate: i === 1 ? '2024-03-15T10:30:00.000Z' : undefined,
      gradedDate: i === 1 ? '2024-03-20T18:00:00.000Z' : undefined,
      weightage: 30,
      actions: {
        created: mockUserAction,
      },
    });
  }

  // Generate 3-4 tests per course
  for (let i = 1; i <= 3; i++) {
    const avgScore = Math.floor(Math.random() * 25) + 75; // 75-100
    assessments.push({
      assessmentId: `ASSESS-COURSE-${courseId}-TEST-${i}`,
      name: `${courseName} - Test ${i}`,
      description: `Periodic test ${i} for ${courseName}`,
      assessmentType: EAssessmentType.TEST,
      status: i <= 2 ? EAssessmentStatus.GRADED : EAssessmentStatus.IN_PROGRESS,
      courseId,
      maxScore: 50,
      totalStudents: Math.floor(Math.random() * 10) + 20,
      averageScore: avgScore * 0.5, // Scaled to 50
      averagePercentage: avgScore,
      scheduledDate: `2024-0${2 + i}-10T09:00:00.000Z`,
      conductedDate: i <= 2 ? `2024-0${2 + i}-10T10:30:00.000Z` : undefined,
      gradedDate: i <= 2 ? `2024-0${2 + i}-12T18:00:00.000Z` : undefined,
      weightage: 15,
      actions: {
        created: mockUserAction,
      },
    });
  }

  // Generate 2-3 assignments per course
  for (let i = 1; i <= 2; i++) {
    const avgScore = Math.floor(Math.random() * 20) + 80; // 80-100
    assessments.push({
      assessmentId: `ASSESS-COURSE-${courseId}-ASSIGN-${i}`,
      name: `${courseName} - Assignment ${i}`,
      description: `Assignment ${i} for ${courseName}`,
      assessmentType: EAssessmentType.ASSIGNMENT,
      status: i === 1 ? EAssessmentStatus.GRADED : EAssessmentStatus.COMPLETED,
      courseId,
      maxScore: 20,
      totalStudents: Math.floor(Math.random() * 10) + 20,
      averageScore: avgScore * 0.2, // Scaled to 20
      averagePercentage: avgScore,
      dueDate: `2024-0${2 + i}-25T17:00:00.000Z`,
      conductedDate: i === 1 ? '2024-02-25T17:00:00.000Z' : '2024-03-25T17:00:00.000Z',
      gradedDate: i === 1 ? '2024-02-28T18:00:00.000Z' : undefined,
      weightage: 10,
      actions: {
        created: mockUserAction,
      },
    });
  }

  // Generate 1-2 quizzes per course
  for (let i = 1; i <= 1; i++) {
    const avgScore = Math.floor(Math.random() * 15) + 85; // 85-100
    assessments.push({
      assessmentId: `ASSESS-COURSE-${courseId}-QUIZ-${i}`,
      name: `${courseName} - Quiz ${i}`,
      description: `Quick quiz ${i} for ${courseName}`,
      assessmentType: EAssessmentType.QUIZ,
      status: EAssessmentStatus.GRADED,
      courseId,
      maxScore: 10,
      totalStudents: Math.floor(Math.random() * 10) + 20,
      averageScore: avgScore * 0.1, // Scaled to 10
      averagePercentage: avgScore,
      conductedDate: `2024-0${2 + i}-05T10:00:00.000Z`,
      gradedDate: `2024-0${2 + i}-06T18:00:00.000Z`,
      weightage: 5,
      actions: {
        created: mockUserAction,
      },
    });
  }

  // Generate 1 project per course (for some courses)
  if (courseId % 3 === 0) {
    const avgScore = Math.floor(Math.random() * 20) + 75; // 75-95
    assessments.push({
      assessmentId: `ASSESS-COURSE-${courseId}-PROJECT-1`,
      name: `${courseName} - Final Project`,
      description: `Final project for ${courseName}`,
      assessmentType: EAssessmentType.PROJECT,
      status: EAssessmentStatus.COMPLETED,
      courseId,
      maxScore: 50,
      totalStudents: Math.floor(Math.random() * 5) + 15,
      averageScore: avgScore * 0.5, // Scaled to 50
      averagePercentage: avgScore,
      dueDate: '2024-04-30T17:00:00.000Z',
      conductedDate: '2024-04-30T17:00:00.000Z',
      weightage: 20,
      actions: {
        created: mockUserAction,
      },
    });
  }

  return assessments;
};

// Generate course assessment maps
export const academicsAssessmentCoursesMock: IAcademicsAssessmentCourseMap[] =
  mockAcademicsCourses.map((course) => {
    const assessments = generateCourseAssessments(course.id, course.name);

    // Group assessments by type
    const assessmentsByType: {
      [key in EAssessmentType]?: IAcademicsAssessment[];
    } = {};
    assessments.forEach((assessment) => {
      if (!assessmentsByType[assessment.assessmentType]) {
        assessmentsByType[assessment.assessmentType] = [];
      }
      assessmentsByType[assessment.assessmentType]!.push(assessment);
    });

    // Calculate aggregated statistics
    const gradedAssessments = assessments.filter(
      (a) => a.status === EAssessmentStatus.GRADED
    );
    const completedAssessments = assessments.filter(
      (a) =>
        a.status === EAssessmentStatus.GRADED ||
        a.status === EAssessmentStatus.COMPLETED
    );

    const totalAssessments = assessments.length;
    const averageScore =
      gradedAssessments.length > 0
        ? gradedAssessments.reduce((sum, a) => sum + (a.averageScore || 0), 0) /
          gradedAssessments.length
        : 0;
    const averagePercentage =
      gradedAssessments.length > 0
        ? gradedAssessments.reduce(
            (sum, a) => sum + (a.averagePercentage || 0),
            0
          ) / gradedAssessments.length
        : 0;
    const completionRate =
      totalAssessments > 0
        ? (completedAssessments.length / totalAssessments) * 100
        : 0;

    return {
      id: course.id,
      courseDetails: {
        name: course.name,
        subjectCode: course.subjectCode,
        subjectType: course.subjectType,
        classIds: course.classIds,
      },
      assessments: assessmentsByType,
      totalAssessments,
      averageScore: Math.round(averageScore * 100) / 100,
      averagePercentage: Math.round(averagePercentage * 100) / 100,
      completionRate: Math.round(completionRate * 100) / 100,
    };
  });

// Helper function to get assessments by course ID
export const getAssessmentsByCourseId = (
  courseId: number
): IAcademicsAssessmentCourseMap | undefined =>
  academicsAssessmentCoursesMock.find((course) => course.id === courseId);

// Helper function to get all assessments for a course
export const getAllAssessmentsByCourseId = (
  courseId: number
): IAcademicsAssessment[] => {
  const courseMap = getAssessmentsByCourseId(courseId);
  if (!courseMap) return [];

  return Object.values(courseMap.assessments).flat();
};

export const academicsAssessmentCourseStatisticsMock: IAcademicsAssessmentStatistics =
  {
    totalAssessments: academicsAssessmentCoursesMock.reduce(
      (sum, course) => sum + course.totalAssessments,
      0
    ),
    totalClasses: academicsAssessmentCoursesMock.length,
    totalCourses: academicsAssessmentCoursesMock.length,
    averageScore:
      academicsAssessmentCoursesMock.reduce(
        (sum, course) => sum + course.averageScore,
        0
      ) / academicsAssessmentCoursesMock.length,
    averagePercentage:
      academicsAssessmentCoursesMock.reduce(
        (sum, course) => sum + course.averagePercentage,
        0
      ) / academicsAssessmentCoursesMock.length,
    completionRate:
      academicsAssessmentCoursesMock.reduce(
        (sum, course) => sum + course.completionRate,
        0
      ) / academicsAssessmentCoursesMock.length,
    scheduledAssessments: academicsAssessmentCoursesMock.reduce(
      (sum, course) =>
        sum +
        Object.values(course.assessments)
          .flat()
          .filter((a) => a.status === EAssessmentStatus.SCHEDULED).length,
      0
    ),
    completedAssessments: academicsAssessmentCoursesMock.reduce(
      (sum, course) =>
        sum +
        Object.values(course.assessments)
          .flat()
          .filter((a) => a.status === EAssessmentStatus.COMPLETED).length,
      0
    ),
    inProgressAssessments: academicsAssessmentCoursesMock.reduce(
      (sum, course) =>
        sum +
        Object.values(course.assessments)
          .flat()
          .filter((a) => a.status === EAssessmentStatus.IN_PROGRESS).length,
      0
    ),
    assessmentsByType: academicsAssessmentCoursesMock.reduce(
      (acc, course) => {
        Object.keys(course.assessments).forEach((type) => {
          acc[type as EAssessmentType] =
            (acc[type as EAssessmentType] || 0) +
            course.assessments[type as EAssessmentType]!.length;
        });
        return acc;
      },
      {} as { [key in EAssessmentType]?: number }
    ),
  };
